import type { ReactNode } from "react";

import { ChevronDownIcon } from "@/components/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/*
 * The quiet layer's flat sections (renders 12 and 13): a header row with the
 * title, one ink3 line and one quiet action on the right, then hairline rows.
 * No boxes, no table heads.
 */

/** The mock's primary button: ink, 30px, radius 8, 13px at 550. */
export const inkButton =
  "btn-ink h-[30px] gap-1.5 rounded-md px-3 font-[550] tracking-[-0.005em]";

/** The mock's large primary button: 36px, radius 9, 13.5px at 550. */
export const inkButtonLarge =
  "btn-ink h-9 gap-1.5 rounded-[9px] px-4 text-[13.5px] font-[550] tracking-[-0.005em]";

/** A section: h2, one line beside it, one quiet action on the right. */
export function QuietSection({
  action,
  children,
  className,
  description,
  title,
  titleId,
}: {
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  readonly description?: ReactNode;
  readonly title?: string;
  readonly titleId: string;
}) {
  return (
    <section
      aria-labelledby={title === undefined ? undefined : titleId}
      className={cn("quiet-section", className)}
    >
      {title === undefined ? null : (
        <header className="quiet-section-head">
          <h2 id={titleId} tabIndex={-1}>
            {title}
          </h2>
          {description === undefined ? null : <p>{description}</p>}
          {action === undefined ? null : (
            <div className="quiet-section-action">{action}</div>
          )}
        </header>
      )}
      {children}
    </section>
  );
}

/** Hairline rows. `label` names the list for screen readers. */
export function QuietRows({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label: string;
}) {
  return (
    <ul aria-label={label} className="quiet-rows">
      {children}
    </ul>
  );
}

/** One `1fr 180px 150px` row: the name, one detail, the text actions. */
export function QuietRow({
  actions,
  data,
  detail,
  dimmed = false,
  name,
  title,
}: {
  readonly actions?: ReactNode;
  /** Data attributes for the row, such as `data-invitation-id`. */
  readonly data?: Readonly<Record<`data-${string}`, string>>;
  readonly detail: ReactNode;
  readonly dimmed?: boolean;
  readonly name: ReactNode;
  readonly title?: string;
}) {
  return (
    <li className="quiet-row" data-dimmed={dimmed} title={title} {...data}>
      <div className="quiet-row-name">{name}</div>
      <span className="quiet-row-detail">{detail}</span>
      <span className="quiet-row-actions">{actions}</span>
    </li>
  );
}

/** One quiet line where rows would be: empty, unavailable, or a gate. */
export function QuietLine({
  action,
  children,
  tone = "quiet",
}: {
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly tone?: "quiet" | "danger";
}) {
  return (
    <p
      className="quiet-line"
      data-tone={tone}
      role={tone === "danger" ? "alert" : undefined}
    >
      <span>{children}</span>
      {action}
    </p>
  );
}

/** Two skeleton rows while a list loads. */
export function QuietLoading({ label }: { readonly label: string }) {
  return (
    <div
      aria-busy="true"
      aria-label={label}
      className="quiet-loading"
      role="status"
    >
      {[0, 1].map((row) => (
        <div className="quiet-loading-row" key={row}>
          <Skeleton className="h-3.5 w-48" />
          <Skeleton className="h-3 w-24" />
        </div>
      ))}
    </div>
  );
}

/** History behind a quiet disclosure at the section end. */
export function QuietHistory({
  children,
  summary,
}: {
  readonly children: ReactNode;
  readonly summary: string;
}) {
  return (
    <details className="quiet-history">
      <summary>
        {summary}
        <ChevronDownIcon className="quiet-history-chevron" />
      </summary>
      {children}
    </details>
  );
}
